import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Edit, Trash2, Save, X, Eye, EyeOff, Upload, Download, SlidersHorizontal, ChevronDown, Search } from 'lucide-react';
import type { MenuItem, Category, MenuItemCustomizationGroup } from '../../App';
import { api, menuAPI } from '../../lib/api';
import { masterDataAPI } from '../../lib/masterDataApi';
import { resolveAssetUrl } from '../../lib/assets';
import { ImageWithFallback } from '../figma/ImageWithFallback';

interface MenuManagementProps {
  restaurantId: string;
  readOnly?: boolean;
}

type RawRecord = Record<string, any>;
type SpicyLevelOption = { value: number; label: string };
type CustomizationOptionForm = {
  id?: string;
  name: string;
  price: number;
  isDefault: boolean;
  sortOrder: number;
};

type CustomizationGroupForm = {
  id?: string;
  name: string;
  displayName: string;
  inputType: 'single' | 'multi';
  isRequired: boolean;
  minSelect: number;
  maxSelect: string;
  sortOrder: number;
  options: CustomizationOptionForm[];
};

const DEFAULT_SERVING_SIZES = ['Serves 1', 'Serves 1-2', 'Serves 2', 'Serves 2-3', 'Serves 3-4', 'Serves 4+'];

const getString = (...values: any[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value;
  }
  return '';
};

const getNumber = (...values: any[]): number => {
  for (const value of values) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return 0;
};

const getBoolean = (...values: any[]): boolean => {
  for (const value of values) {
    if (typeof value === 'boolean') return value;
    if (value === 1 || value === '1') return true;
    if (value === 0 || value === '0') return false;
  }
  return false;
};

const parseJsonArray = <T,>(value: any): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const mapCustomizationGroupFromApi = (group: RawRecord): MenuItemCustomizationGroup => ({
  id: getString(group.id),
  name: getString(group.name),
  displayName: getString(group.displayName, group.display_name, group.name),
  inputType: getString(group.inputType, group.input_type) === 'single' ? 'single' : 'multi',
  isRequired: getBoolean(group.isRequired, group.is_required),
  minSelect: getNumber(group.minSelect, group.min_select, 0),
  maxSelect:
    group.maxSelect === null || group.max_select === null || group.maxSelect === ''
      ? null
      : getNumber(group.maxSelect, group.max_select),
  sortOrder: getNumber(group.sortOrder, group.sort_order, 0),
  options: parseJsonArray<any>(group.options).map((option) => ({
    id: getString(option.id),
    name: getString(option.name),
    price: getNumber(option.price, 0),
    isDefault: getBoolean(option.isDefault, option.is_default),
    sortOrder: getNumber(option.sortOrder, option.sort_order, 0),
    category: getString(group.displayName, group.display_name, group.name),
  })),
});

const mapCategoryFromApi = (category: RawRecord): Category => ({
  id: getString(category.id),
  restaurantId: getString(category.restaurant_id, category.restaurantId),
  name: getString(category.name),
  image: resolveAssetUrl(getString(category.image, category.image_url)) || undefined,
  order: getNumber(category.display_order, category.displayOrder),
  isActive: getBoolean(category.is_active, category.isActive, true),
});

const mapMenuItemFromApi = (item: RawRecord, restaurantId: string): MenuItem => ({
  id: getString(item.id),
  restaurantId: getString(item.restaurant_id, item.restaurantId, restaurantId),
  categoryId: getString(
    item.category?.food_category_id,
    item.food_category_id,
    item.foodCategoryId,
    item.food_categoryId,
    item.category?.id,
    item.categoryId,
    item.category_id,
    item.category_id
  ),
  name: getString(item.name),
  description: getString(item.description),
  price: getNumber(item.price),
  image: resolveAssetUrl(getString(item.image)) || undefined,
  tags: parseJsonArray<string>(item.tags),
  spicyLevel: getNumber(item.spicy_level, item.spicyLevel, 0),
  isActive: getBoolean(item.is_active, item.isActive, true),
  preparationTime: getNumber(item.preparation_time, item.preparationTime, 15),
  servingSize: getString(item.servingSize, item.serving_size) || null,
  inStock: getBoolean(item.in_stock, item.inStock, true),
  variants: parseJsonArray<any>(item.variants).map((variant) => ({
    id: getString(variant.id),
    name: getString(variant.name),
    price: getNumber(variant.price, variant.price_adjustment),
    isDefault: getBoolean(variant.is_default, variant.isDefault),
  })),
  addOns: parseJsonArray<any>(item.addons).map((addon) => ({
    id: getString(addon.id),
    name: getString(addon.name),
    price: getNumber(addon.price),
    category: getString(addon.category, 'Extras'),
  })),
  customizationGroups: parseJsonArray<any>(item.customizationGroups ?? item.customization_groups).map(
    mapCustomizationGroupFromApi
  ),
});

const getDefaultForm = (): Partial<MenuItem> => ({
  tags: [],
  spicyLevel: 0,
  isActive: true,
  inStock: true,
  preparationTime: 15,
  servingSize: '',
  customizationGroups: [],
});

const getDefaultCustomizationGroupForm = (): CustomizationGroupForm => ({
  name: '',
  displayName: '',
  inputType: 'multi',
  isRequired: false,
  minSelect: 0,
  maxSelect: '',
  sortOrder: 0,
  options: [{ name: '', price: 0, isDefault: false, sortOrder: 0 }],
});

export function MenuManagement({ restaurantId, readOnly = false }: MenuManagementProps) {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([
    'veg',
    'non-veg',
    'jain',
    'bestseller',
    'chef-special',
    'seasonal',
    'recommended',
  ]);
  const [spicyLevels, setSpicyLevels] = useState<SpicyLevelOption[]>([
    { value: 0, label: 'Not Spicy' },
    { value: 1, label: 'Mild' },
    { value: 2, label: 'Medium' },
    { value: 3, label: 'Very Spicy' },
  ]);
  const [servingSizeOptions, setServingSizeOptions] = useState<string[]>(DEFAULT_SERVING_SIZES);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [form, setForm] = useState<Partial<MenuItem>>(getDefaultForm());
  const [customizationGroups, setCustomizationGroups] = useState<MenuItemCustomizationGroup[]>([]);
  const [showCustomizationForm, setShowCustomizationForm] = useState(false);
  const [isSavingCustomization, setIsSavingCustomization] = useState(false);
  const [editingCustomizationId, setEditingCustomizationId] = useState<string | null>(null);
  const [customizationForm, setCustomizationForm] = useState<CustomizationGroupForm>(getDefaultCustomizationGroupForm());
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadedImageFileName, setUploadedImageFileName] = useState('');
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const [modifierGroupQuery, setModifierGroupQuery] = useState('');
  const [isModifierGroupDropdownOpen, setIsModifierGroupDropdownOpen] = useState(false);
  const modifierGroupDropdownRef = useRef<HTMLDivElement | null>(null);

  const resetForm = () => {
    setForm(getDefaultForm());
    setEditingId(null);
    setIsAdding(false);
    setPendingImageFile(null);
    setUploadedImageFileName('');
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const resetCustomizationForm = () => {
    setCustomizationForm(getDefaultCustomizationGroupForm());
    setEditingCustomizationId(null);
    setShowCustomizationForm(false);
  };

  const readFileAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleImageUpload = async () => {
    if (!pendingImageFile) return;
    if (!pendingImageFile.type.startsWith('image/')) {
      setErrorMessage('Please upload a valid image file');
      return;
    }

    setIsUploadingImage(true);
    setErrorMessage('');
    try {
      const dataUrl = await readFileAsDataUrl(pendingImageFile);
      const response = await api.restaurants.uploadImage({
        imageData: dataUrl,
        fileName: pendingImageFile.name,
      });
      const uploadedUrl = response?.data?.url || '';
      const absoluteUrl = resolveAssetUrl(uploadedUrl);

      setForm((previous) => ({ ...previous, image: absoluteUrl }));
      setUploadedImageFileName(response?.data?.fileName || pendingImageFile.name);
      setPendingImageFile(null);
      if (imageInputRef.current) imageInputRef.current.value = '';
    } catch {
      setErrorMessage('Unable to upload selected image');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageDownload = async () => {
    if (!form.image) return;
    try {
      const response = await fetch(form.image);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const fileNameFromUrl = String(form.image).split('?')[0].split('/').pop() || 'menu-item-image';
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = uploadedImageFileName || fileNameFromUrl;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      setErrorMessage('Unable to download image');
    }
  };

  const loadMenuData = useCallback(async () => {
    if (!restaurantId) return;

    setIsLoading(true);
    setErrorMessage('');

    try {
      const [itemsResult, masterCategoriesResult, dietaryTagsResult, spiceLevelsResult, servingSizesResult, customizationGroupsResult] =
        await Promise.allSettled([
        // Do not send `isActive` in query: backend should return both active + inactive.
        menuAPI.getItems(restaurantId),
        masterDataAPI.foodCategories.getAll(),
        masterDataAPI.dietaryPreferences.getAll(),
        masterDataAPI.spiceLevels.getAll(),
        masterDataAPI.servingSizes.getAll(),
        api.restaurantCustomizations.getAll(restaurantId),
      ]);

      const rawItems =
        itemsResult.status === 'fulfilled' && Array.isArray(itemsResult.value?.data)
          ? itemsResult.value.data
          : [];
      const rawMasterCategories =
        masterCategoriesResult.status === 'fulfilled' && Array.isArray(masterCategoriesResult.value)
          ? masterCategoriesResult.value
          : [];
      const rawDietaryTags =
        dietaryTagsResult.status === 'fulfilled' && Array.isArray(dietaryTagsResult.value)
          ? dietaryTagsResult.value
          : [];
      const rawSpiceLevels =
        spiceLevelsResult.status === 'fulfilled' && Array.isArray(spiceLevelsResult.value)
          ? spiceLevelsResult.value
          : [];
      const rawServingSizes =
        servingSizesResult.status === 'fulfilled' && Array.isArray(servingSizesResult.value)
          ? servingSizesResult.value
          : [];
      const rawCustomizationGroups =
        customizationGroupsResult.status === 'fulfilled' && Array.isArray(customizationGroupsResult.value?.data)
          ? customizationGroupsResult.value.data
          : [];

      const mappedItems = rawItems.map((item) => mapMenuItemFromApi(item, restaurantId));
      const mappedCategories = rawMasterCategories
        .filter((category: any) => category?.id && category?.name && category?.isActive !== false)
        .map((category: any, index: number) => ({
          id: String(category.id),
          restaurantId,
          name: String(category.name),
          image: undefined,
          order: Number(category.displayOrder ?? index + 1),
          isActive: true,
        }))
        .filter((category) => category?.id && category?.name && category.isActive !== false)
        .filter((category) => category.id && category.name)
        .sort((a, b) => a.order - b.order);

      const fallbackCategories = Array.from(
        mappedItems.reduce((acc, item) => {
          if (!item.categoryId || acc.has(item.categoryId)) return acc;
          const rawItem = rawItems.find(
            (raw) =>
              getString(raw.category?.food_category_id, raw.category?.id, raw.category_id, raw.categoryId) ===
              item.categoryId
          );
          acc.set(item.categoryId, {
            id: item.categoryId,
            restaurantId,
            name: getString(rawItem?.category?.name, rawItem?.category_name, 'Uncategorized'),
            image: undefined,
            order: acc.size + 1,
            isActive: true,
          } as Category);
          return acc;
        }, new Map<string, Category>())
      ).map(([, category]) => category);

      const dynamicTags = rawDietaryTags
        .filter((tag: any) => tag?.name && tag?.isActive !== false)
        .map((tag: any) => String(tag.name));

      const dynamicServingSizes = rawServingSizes
        .filter((size: any) => size?.name && size?.isActive !== false)
        .map((size: any) => String(size.name));
      const dynamicSpicyLevels = rawSpiceLevels
        .filter((level: any) => level?.isActive !== false)
        .map((level: any, index: number) => ({
          value: Number(level.levelValue ?? level.value ?? index),
          label: String(level.name ?? level.label ?? `Level ${index}`),
        }))
        .filter((level: SpicyLevelOption) => Number.isFinite(level.value))
        .sort((a: SpicyLevelOption, b: SpicyLevelOption) => a.value - b.value);

      setMenuItems(mappedItems);
      const mergedCategories = Array.from(
        [...mappedCategories, ...fallbackCategories].reduce((acc, category) => {
          if (!category?.id) return acc;
          if (!acc.has(category.id)) acc.set(category.id, category);
          return acc;
        }, new Map<string, Category>())
      ).map(([, category]) => category);
      setCategories(mergedCategories);
      if (dynamicTags.length > 0) setAvailableTags(dynamicTags);
      if (dynamicSpicyLevels.length > 0) setSpicyLevels(dynamicSpicyLevels);
      if (dynamicServingSizes.length > 0) setServingSizeOptions(dynamicServingSizes);
      setCustomizationGroups(rawCustomizationGroups.map(mapCustomizationGroupFromApi));
    } catch (error: any) {
      console.error('Failed to load menu data', error);
      setErrorMessage(error?.message || 'Failed to load menu data');
    } finally {
      setIsLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    void loadMenuData();
  }, [loadMenuData]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (modifierGroupDropdownRef.current && !modifierGroupDropdownRef.current.contains(target)) {
        setIsModifierGroupDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const handleAdd = async () => {
    if (!form.name || !form.price || !form.categoryId) return;

    setIsSaving(true);
    setErrorMessage('');
    try {
      await menuAPI.createItem({
        restaurantId,
        foodCategoryId: form.categoryId,
        name: form.name,
        description: form.description || '',
        price: Number(form.price),
        image: form.image || null,
        tags: form.tags || [],
        spicyLevel: form.spicyLevel || 0,
        preparationTime: form.preparationTime || 15,
        servingSize: form.servingSize || null,
        customizationGroups: (form.customizationGroups || []).map((group, index) => ({
          groupId: group.id,
          sortOrder: group.sortOrder ?? index,
        })),
      });
      resetForm();
      await loadMenuData();
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to add menu item');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !form.name || !form.price || !form.categoryId) return;

    setIsSaving(true);
    setErrorMessage('');
    try {
      await menuAPI.updateItem(editingId, {
        foodCategoryId: form.categoryId,
        name: form.name,
        description: form.description || '',
        price: Number(form.price),
        image: form.image || null,
        tags: form.tags || [],
        spicy_level: form.spicyLevel || 0,
        is_active: form.isActive !== false,
        preparation_time: form.preparationTime || 15,
        servingSize: form.servingSize || null,
        in_stock: form.inStock !== false,
        customizationGroups: (form.customizationGroups || []).map((group, index) => ({
          groupId: group.id,
          sortOrder: group.sortOrder ?? index,
        })),
      });
      resetForm();
      await loadMenuData();
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to update menu item');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this menu item?')) return;

    setErrorMessage('');
    try {
      await menuAPI.deleteItem(id);
      setMenuItems((previous) => previous.filter((item) => item.id !== id));
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to delete menu item');
    }
  };

  const toggleTag = (tag: string) => {
    const currentTags = form.tags || [];
    setForm({
      ...form,
      tags: currentTags.includes(tag)
        ? currentTags.filter((currentTag) => currentTag !== tag)
        : [...currentTags, tag],
    });
  };

  const toggleItemStatus = async (item: MenuItem) => {
    setErrorMessage('');
    try {
      await menuAPI.updateItem(item.id, { is_active: !item.isActive });
      setMenuItems((previous) =>
        previous.map((existingItem) =>
          existingItem.id === item.id ? { ...existingItem, isActive: !existingItem.isActive } : existingItem
        )
      );
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to update item status');
    }
  };

  const handleCustomizationOptionChange = (index: number, next: Partial<CustomizationOptionForm>) => {
    setCustomizationForm((previous) => ({
      ...previous,
      options: previous.options.map((option, optionIndex) =>
        optionIndex === index ? { ...option, ...next } : option
      ),
    }));
  };

  const handleSaveCustomizationGroup = async () => {
    if (!customizationForm.name.trim() || !customizationForm.displayName.trim()) {
      setErrorMessage('Customization group name and display name are required');
      return;
    }

    if (customizationForm.isRequired && Number(customizationForm.minSelect || 0) < 1) {
      setErrorMessage('Min Select must be at least 1 when Required is enabled');
      return;
    }

    const parsedMaxSelect =
      customizationForm.maxSelect === '' ? null : Number(customizationForm.maxSelect);
    if (parsedMaxSelect !== null && parsedMaxSelect < Number(customizationForm.minSelect || 0)) {
      setErrorMessage('Max Select must be greater than or equal to Min Select');
      return;
    }

    const payload = {
      name: customizationForm.name.trim(),
      displayName: customizationForm.displayName.trim(),
      inputType: customizationForm.inputType,
      isRequired: customizationForm.isRequired,
      minSelect: customizationForm.minSelect,
      maxSelect: parsedMaxSelect,
      sortOrder: customizationForm.sortOrder,
      options: customizationForm.options
        .filter((option) => option.name.trim())
        .map((option, index) => ({
          ...option,
          name: option.name.trim(),
          sortOrder: option.sortOrder ?? index,
        })),
    };

    setIsSavingCustomization(true);
    setErrorMessage('');
    try {
      if (editingCustomizationId) {
        await api.restaurantCustomizations.update(restaurantId, editingCustomizationId, payload);
      } else {
        await api.modifierGroups.create({
          restaurant_id: restaurantId,
          group_name: payload.displayName,
          system_name: payload.name,
          input_type: payload.inputType === 'single' ? 'single_select' : 'multi_select',
          min_select: payload.minSelect,
          max_select: payload.maxSelect,
          is_required: payload.isRequired,
          sort_order: payload.sortOrder,
          options: payload.options.map((option, index) => ({
            option_name: option.name,
            price: Number(option.price || 0),
            is_default: option.isDefault === true,
            sort_order: option.sortOrder ?? index,
          })),
        });
      }
      resetCustomizationForm();
      await loadMenuData();
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to save customization group');
    } finally {
      setIsSavingCustomization(false);
    }
  };

  const handleDeleteCustomizationGroup = async (groupId: string) => {
    if (!confirm('Are you sure you want to delete this customization group?')) return;

    setErrorMessage('');
    try {
      await api.restaurantCustomizations.delete(restaurantId, groupId);
      setCustomizationGroups((previous) => previous.filter((group) => group.id !== groupId));
      setForm((previous) => ({
        ...previous,
        customizationGroups: (previous.customizationGroups || []).filter((group) => group.id !== groupId),
      }));
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to delete customization group');
    }
  };

  const toggleCustomizationSelection = (group: MenuItemCustomizationGroup) => {
    const current = form.customizationGroups || [];
    const exists = current.some((item) => item.id === group.id);
    setForm({
      ...form,
      customizationGroups: exists ? current.filter((item) => item.id !== group.id) : [...current, group],
    });
  };

  const setCustomizationSelection = (next: MenuItemCustomizationGroup[]) => {
    setForm((previous) => ({
      ...previous,
      customizationGroups: next,
    }));
  };

  const groupedCategories = useMemo(
    () =>
      categories
        .map((category) => ({
          ...category,
          items: menuItems.filter((item) => item.categoryId === category.id),
        }))
        .filter((category) => category.items.length > 0),
    [categories, menuItems]
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-gray-900">Menu Management</h1>
        {!readOnly && (
          <button
            onClick={() => {
              setErrorMessage('');
              setForm(getDefaultForm());
              setEditingId(null);
              setIsAdding(true);
              setPendingImageFile(null);
              setUploadedImageFileName('');
              if (imageInputRef.current) imageInputRef.current.value = '';
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Menu Item
          </button>
        )}
      </div>

      {errorMessage && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-red-700 text-sm">
          {errorMessage}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200/60 bg-gradient-to-br from-white to-slate-50/60 shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
              <SlidersHorizontal className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-gray-900">Modifier Groups</h3>
              <p className="text-sm text-gray-600">
                Menu item customizations (portion, extras, toppings) yahin se maintain honge.
              </p>
            </div>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={() => {
                setShowCustomizationForm(true);
                setEditingCustomizationId(null);
                setCustomizationForm(getDefaultCustomizationGroupForm());
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
            >
              <Plus className="w-4 h-4" />
              Add Modifier Group
            </button>
          )}
        </div>

        {showCustomizationForm && !readOnly && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-[2px] p-3 sm:p-5"
            onClick={resetCustomizationForm}
          >
            <div
              className="w-full max-w-3xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between border-b border-slate-100 bg-white px-5 py-4">
                <div className="pr-4">
                  <h3 className="text-gray-900">{editingCustomizationId ? 'Edit Modifier Group' : 'Add Modifier Group'}</h3>
                  <p className="text-sm text-gray-600">Create modifier group + options for menu item customization.</p>
                </div>
                <button
                  type="button"
                  onClick={resetCustomizationForm}
                  className="rounded-xl p-2 text-slate-600 transition-colors hover:bg-slate-100"
                  aria-label="Close modifier group modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="max-h-[75vh] overflow-y-auto px-5 py-5 space-y-5">
                {errorMessage && (
                  <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-red-700 text-sm">
                    {errorMessage}
                  </div>
                )}
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <h4 className="text-gray-900 mb-4">Group Information</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Group Name</label>
                      <input
                        type="text"
                        value={customizationForm.displayName}
                        onChange={(e) => setCustomizationForm((previous) => ({ ...previous, displayName: e.target.value }))}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                        placeholder="Extra Toppings"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">System Name</label>
                      <input
                        type="text"
                        value={customizationForm.name}
                        onChange={(e) => setCustomizationForm((previous) => ({ ...previous, name: e.target.value }))}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                        placeholder="extra_toppings"
                      />
                      <p className="mt-1 text-xs text-slate-500">Internal key (snake_case recommended).</p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Input Type</label>
                      <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-1">
                        <button
                          type="button"
                          onClick={() => setCustomizationForm((previous) => ({ ...previous, inputType: 'single' }))}
                          className={`flex-1 rounded-lg px-3 py-2 text-sm transition-all ${
                            customizationForm.inputType === 'single'
                              ? 'bg-white text-slate-900 shadow-sm'
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          Single select
                        </button>
                        <button
                          type="button"
                          onClick={() => setCustomizationForm((previous) => ({ ...previous, inputType: 'multi' }))}
                          className={`flex-1 rounded-lg px-3 py-2 text-sm transition-all ${
                            customizationForm.inputType === 'multi'
                              ? 'bg-white text-slate-900 shadow-sm'
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          Multi select
                        </button>
                      </div>
                    </div>

                    <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">Required</p>
                        <p className="text-xs text-slate-500">Customer must select at least one option.</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={customizationForm.isRequired}
                        onChange={(e) => setCustomizationForm((previous) => ({ ...previous, isRequired: e.target.checked }))}
                        className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-4 focus:ring-blue-500/10"
                      />
                    </label>
                  </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Min Select</label>
                        <input
                          type="number"
                          value={customizationForm.minSelect}
                          onChange={(e) =>
                            setCustomizationForm((previous) => ({ ...previous, minSelect: Number(e.target.value || 0) }))
                          }
                          className="no-spinner w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                          placeholder="Min Select"
                          min={0}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Max Select</label>
                        <input
                          type="number"
                          value={customizationForm.maxSelect}
                          onChange={(e) => setCustomizationForm((previous) => ({ ...previous, maxSelect: e.target.value }))}
                          className="no-spinner w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                          placeholder="Max Select"
                          min={0}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Sort Order</label>
                        <input
                          type="number"
                          value={customizationForm.sortOrder}
                          onChange={(e) =>
                            setCustomizationForm((previous) => ({ ...previous, sortOrder: Number(e.target.value || 0) }))
                          }
                          className="no-spinner w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                          placeholder="Sort Order"
                          min={0}
                        />
                      </div>
                    </div>
                  </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-gray-900">Options</h4>
                    <button
                      type="button"
                      onClick={() =>
                        setCustomizationForm((previous) => ({
                          ...previous,
                          options: [
                            ...previous.options,
                            { name: '', price: 0, isDefault: false, sortOrder: previous.options.length },
                          ],
                        }))
                      }
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50"
                    >
                      <Plus className="h-4 w-4" />
                      Add Option
                    </button>
                  </div>
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    <div className="grid grid-cols-12 items-center gap-4 bg-slate-50 px-4 py-2.5 text-xs font-semibold text-slate-600">
                      <div className="col-span-5">Option</div>
                      <div className="col-span-2">Price</div>
                      <div className="col-span-2">Sort</div>
                      <div className="col-span-1 text-center">Default</div>
                      <div className="col-span-2 text-center">Remove</div>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {customizationForm.options.map((option, index) => (
                        <div key={`${option.id || 'new'}-${index}`} className="grid grid-cols-12 items-center gap-4 px-4 py-3">
                          <div className="col-span-12 md:col-span-5">
                            <input
                              type="text"
                              value={option.name}
                              onChange={(e) => handleCustomizationOptionChange(index, { name: e.target.value })}
                              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                              placeholder="e.g., Mushrooms"
                            />
                          </div>
                          <div className="col-span-6 md:col-span-2">
                            <input
                              type="number"
                              value={option.price}
                              onChange={(e) =>
                                handleCustomizationOptionChange(index, { price: Number(e.target.value || 0) })
                              }
                              className="no-spinner h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                              placeholder="Price"
                              min={0}
                            />
                          </div>
                          <div className="col-span-6 md:col-span-2">
                            <input
                              type="number"
                              value={option.sortOrder}
                              onChange={(e) =>
                                handleCustomizationOptionChange(index, { sortOrder: Number(e.target.value || 0) })
                              }
                              className="no-spinner h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                              placeholder="Sort"
                              min={0}
                            />
                          </div>
                          <div className="col-span-6 md:col-span-1 flex h-10 items-center justify-start md:justify-center">
                            <input
                              type="checkbox"
                              checked={option.isDefault}
                              onChange={(e) => handleCustomizationOptionChange(index, { isDefault: e.target.checked })}
                              className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-4 focus:ring-blue-500/10"
                            />
                          </div>
                          <div className="col-span-6 md:col-span-2 flex h-10 items-center justify-end">
                            <button
                              type="button"
                              onClick={() =>
                                setCustomizationForm((previous) => ({
                                  ...previous,
                                  options: previous.options.filter((_, optionIndex) => optionIndex !== index),
                                }))
                              }
                              className="h-10 rounded-xl border border-red-200 bg-red-50 px-3 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-white px-5 py-4">
                <button
                  type="button"
                  onClick={resetCustomizationForm}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleSaveCustomizationGroup()}
                  disabled={isSavingCustomization}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-60"
                >
                  <Save className="w-4 h-4" />
                  {isSavingCustomization ? 'Saving...' : editingCustomizationId ? 'Update Group' : 'Create Group'}
                </button>
              </div>
            </div>
          </div>
        )}

        {customizationGroups.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
            <p className="text-sm font-semibold text-slate-900">No modifier groups yet</p>
            <p className="mt-1 text-sm text-slate-600">Create groups like Portion Size, Extras, Spice Level, Toppings.</p>
            {!readOnly && (
              <button
                type="button"
                onClick={() => {
                  setShowCustomizationForm(true);
                  setEditingCustomizationId(null);
                  setCustomizationForm(getDefaultCustomizationGroupForm());
                }}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
              >
                <Plus className="h-4 w-4" />
                Add Modifier Group
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {customizationGroups.map((group) => {
              const badges = [
                {
                  label: group.inputType === 'single' ? 'Single select' : 'Multi select',
                  className: 'bg-slate-100 text-slate-700 border-slate-200',
                },
                ...(group.isRequired
                  ? [{ label: 'Required', className: 'bg-blue-50 text-blue-700 border-blue-100' }]
                  : []),
                {
                  label:
                    group.maxSelect === null || group.maxSelect === undefined
                      ? `Min ${group.minSelect || 0} | Max unlimited`
                      : `Min ${group.minSelect || 0} | Max ${group.maxSelect}`,
                  className: 'bg-emerald-50 text-emerald-700 border-emerald-100',
                },
              ];

              const optionsPreview = (group.options || [])
                .slice()
                .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
              const visibleOptions = optionsPreview.slice(0, 6);
              const remaining = Math.max(0, optionsPreview.length - visibleOptions.length);

              return (
                <div
                  key={group.id}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="truncate text-base font-semibold text-slate-900">{group.displayName}</h4>
                        <span className="hidden sm:inline text-xs text-slate-500">|</span>
                        <span className="hidden sm:inline text-xs text-slate-500">{group.name}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {badges.map((badge) => (
                          <span
                            key={badge.label}
                            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                        ))}
                      </div>
                    </div>

                    {!readOnly && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingCustomizationId(group.id);
                            setShowCustomizationForm(true);
                            setCustomizationForm({
                              id: group.id,
                              name: group.name,
                              displayName: group.displayName,
                              inputType: group.inputType,
                              isRequired: group.isRequired,
                              minSelect: group.minSelect,
                              maxSelect:
                                group.maxSelect === null || group.maxSelect === undefined ? '' : String(group.maxSelect),
                              sortOrder: group.sortOrder,
                              options: group.options.map((option, index) => ({
                                id: option.id,
                                name: option.name,
                                price: option.price,
                                isDefault: option.isDefault === true,
                                sortOrder: option.sortOrder ?? index,
                              })),
                            });
                          }}
                          className="rounded-xl border border-slate-200 bg-white p-2 text-slate-700 transition-colors hover:bg-slate-50"
                          aria-label="Edit modifier group"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDeleteCustomizationGroup(group.id)}
                          className="rounded-xl border border-red-200 bg-red-50 p-2 text-red-700 transition-colors hover:bg-red-100"
                          aria-label="Delete modifier group"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {visibleOptions.map((option) => (
                      <span
                        key={option.id}
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700"
                      >
                        <span className="truncate max-w-[150px]">{option.name}</span>
                        {option.price > 0 && <span className="text-slate-500">+Rs {option.price}</span>}
                        {option.isDefault === true && <span className="text-blue-600">(default)</span>}
                      </span>
                    ))}
                    {remaining > 0 && (
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                        +{remaining} more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {(isAdding || editingId) && !readOnly && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-[2px] p-3 sm:p-5"
          onClick={resetForm}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-slate-100 bg-white px-5 py-4">
              <div className="pr-4">
                <h3 className="text-gray-900">{isAdding ? 'Add New Item' : 'Edit Item'}</h3>
                <p className="text-sm text-gray-600">Menu item details yahin update honge.</p>
              </div>
              <button
                type="button"
                onClick={resetForm}
                className="rounded-xl p-2 text-slate-600 transition-colors hover:bg-slate-100"
                aria-label="Close menu item modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="max-h-[75vh] overflow-y-auto px-5 py-5">
              {errorMessage && (
                <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-red-700 text-sm">
                  {errorMessage}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-700 mb-2">Item Name</label>
              <input
                type="text"
                value={form.name || ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Paneer Tikka"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Price (Rs)</label>
              <input
                type="number"
                value={form.price || ''}
                onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="280"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-2">Description</label>
              <textarea
                value={form.description || ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Describe the dish..."
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Category</label>
              <select
                value={form.categoryId || ''}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Preparation Time (mins)</label>
              <input
                type="number"
                value={form.preparationTime || 15}
                onChange={(e) => setForm({ ...form, preparationTime: parseInt(e.target.value, 10) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Serving Info / Serves (People)</label>
              <input
                type="text"
                value={String(form.servingSize ?? '')}
                onChange={(e) => setForm({ ...form, servingSize: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Serves 1-2"
              />
              {servingSizeOptions.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Examples: {servingSizeOptions.slice(0, 4).join(', ')}
                </p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-2">Image Upload</label>
              {!form.image ? (
                <div className="flex items-center gap-3">
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPendingImageFile(e.target.files?.[0] || null)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => void handleImageUpload()}
                    disabled={!pendingImageFile || isUploadingImage}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg disabled:opacity-50"
                  >
                    <Upload className="w-4 h-4" />
                    {isUploadingImage ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    readOnly
                    value={
                      uploadedImageFileName ||
                      String(form.image || '')
                        .split('?')[0]
                        .split('/')
                        .pop() ||
                      String(form.image || '')
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white text-gray-700"
                  />
                  <button
                    type="button"
                    onClick={() => void handleImageDownload()}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setForm((previous) => ({ ...previous, image: '' }));
                      setUploadedImageFileName('');
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-2">Tags</label>
              <div className="flex flex-wrap gap-2">
                {availableTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1.5 rounded-full border transition-colors ${
                      (form.tags || []).includes(tag)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Spicy Level</label>
              <input
                type="range"
                min={spicyLevels[0]?.value ?? 0}
                max={spicyLevels[spicyLevels.length - 1]?.value ?? 3}
                value={form.spicyLevel || 0}
                onChange={(e) => setForm({ ...form, spicyLevel: parseInt(e.target.value, 10) })}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-600 mt-1">
                {spicyLevels.map((level) => (
                  <span key={level.value}>{level.label}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.isActive !== false}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-700">Active</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.inStock !== false}
                  onChange={(e) => setForm({ ...form, inStock: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-700">In Stock</span>
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-2">Attach Modifier Groups</label>
              <div ref={modifierGroupDropdownRef} className="relative max-w-xl">
                {(() => {
                  const selected = form.customizationGroups || [];
                  const selectedText = selected.map((g) => g.displayName).filter(Boolean).join(', ');
                  return (
                    <button
                      type="button"
                      onClick={() => setIsModifierGroupDropdownOpen((prev) => !prev)}
                      className={`flex w-full items-center justify-between gap-3 rounded-lg border bg-white px-3 py-2.5 text-left text-sm transition-shadow ${
                        isModifierGroupDropdownOpen
                          ? 'border-purple-500 ring-2 ring-purple-500/20'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      aria-haspopup="listbox"
                      aria-expanded={isModifierGroupDropdownOpen}
                    >
                      <span className={`min-w-0 flex-1 truncate ${selectedText ? 'text-gray-900' : 'text-gray-500'}`}>
                        {selectedText || 'Select modifier groups'}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${
                          isModifierGroupDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                  );
                })()}

                {isModifierGroupDropdownOpen && (
                  <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
                    {(() => {
                      const query = modifierGroupQuery.trim().toLowerCase();
                      const visibleGroups = customizationGroups.filter((group) => {
                        if (!query) return true;
                        return (
                          String(group.displayName || '').toLowerCase().includes(query) ||
                          String(group.name || '').toLowerCase().includes(query)
                        );
                      });
                      const selectedIds = new Set((form.customizationGroups || []).map((g) => g.id));
                      const allVisibleSelected =
                        visibleGroups.length > 0 && visibleGroups.every((group) => selectedIds.has(group.id));

                      const toggleAllVisible = () => {
                        const selected = form.customizationGroups || [];
                        const visibleIds = new Set(visibleGroups.map((g) => g.id));
                        if (allVisibleSelected) {
                          setCustomizationSelection(selected.filter((g) => !visibleIds.has(g.id)));
                          return;
                        }
                        const next = [...selected];
                        const existing = new Set(selected.map((g) => g.id));
                        visibleGroups.forEach((g) => {
                          if (!existing.has(g.id)) next.push(g);
                        });
                        setCustomizationSelection(next);
                      };

                      return (
                        <div>
                          <div className="flex items-center gap-2 border-b border-gray-100 p-2">
                            <button
                              type="button"
                              onClick={toggleAllVisible}
                              className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                              aria-label={allVisibleSelected ? 'Unselect all' : 'Select all'}
                              title={allVisibleSelected ? 'Unselect all' : 'Select all'}
                            >
                              <input
                                type="checkbox"
                                checked={allVisibleSelected}
                                onChange={() => undefined}
                                className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                              />
                            </button>

                            <div className="relative flex-1">
                              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                              <input
                                value={modifierGroupQuery}
                                onChange={(e) => setModifierGroupQuery(e.target.value)}
                                placeholder="Search modifier groups..."
                                className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                              />
                            </div>

                            {(form.customizationGroups || []).length > 0 && (
                              <button
                                type="button"
                                onClick={() => setCustomizationSelection([])}
                                className="flex h-10 items-center justify-center rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                title="Clear selected"
                              >
                                Clear
                              </button>
                            )}
                          </div>

                          <div className="max-h-56 overflow-y-auto p-1">
                            {visibleGroups.map((group) => {
                              const isSelected = selectedIds.has(group.id);
                              return (
                                <button
                                  key={group.id}
                                  type="button"
                                  onClick={() => toggleCustomizationSelection(group)}
                                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                                  role="option"
                                  aria-selected={isSelected}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => undefined}
                                    className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                  />
                                  <span className="truncate">{group.displayName}</span>
                                </button>
                              );
                            })}

                            {visibleGroups.length === 0 && (
                              <div className="px-3 py-6 text-center text-sm text-gray-500">No modifier groups found</div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 bg-white px-5 py-4">
              <button
                type="button"
                onClick={resetForm}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={isAdding ? handleAdd : handleUpdate}
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-60"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving...' : isAdding ? 'Add Item' : 'Update Item'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 text-gray-600">Loading menu...</div>
      )}

      {!isLoading &&
        groupedCategories.map((category) => (
          <div key={category.id} className="mb-8">
            <h2 className="text-gray-900 mb-4">
              {category.name} ({category.items.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {category.items.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl shadow-sm overflow-hidden"
                >
                  {item.image && (
                    <div className="h-40 bg-gray-200">
                      <ImageWithFallback src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-gray-900 mb-1">{item.name}</h3>
                        <p className="text-gray-600 text-sm line-clamp-2 mb-2">{item.description}</p>
                      </div>
                      {!item.isActive && (
                        <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded ml-2">
                          Inactive
                        </span>
                      )}
                      {!item.inStock && (
                        <span className="bg-orange-100 text-orange-700 text-xs px-2 py-1 rounded ml-2">
                          Out of Stock
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(item.tags || []).slice(0, 3).map((tag) => (
                        <span key={tag} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-900">Rs{item.price}</span>
                      <span className="text-gray-600 text-sm">{item.preparationTime} mins</span>
                    </div>
                    {(item.customizationGroups || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {item.customizationGroups?.map((group) => (
                          <span key={group.id} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                            {group.displayName}
                          </span>
                        ))}
                      </div>
                    )}
                    {!readOnly && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setErrorMessage('');
                            setEditingId(item.id);
                            setIsAdding(false);
                            setForm({ ...item, tags: item.tags || [] });
                            setPendingImageFile(null);
                            if (imageInputRef.current) imageInputRef.current.value = '';
                          }}
                          className="flex-1 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4 mx-auto" />
                        </button>
                        <button
                          onClick={() => void toggleItemStatus(item)}
                          className="flex-1 p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        >
                          {item.isActive ? (
                            <EyeOff className="w-4 h-4 mx-auto" />
                          ) : (
                            <Eye className="w-4 h-4 mx-auto" />
                          )}
                        </button>
                        <button
                          onClick={() => void handleDelete(item.id)}
                          className="flex-1 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

      {!isLoading && menuItems.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <p className="text-gray-600">No menu items yet. Add your first item!</p>
        </div>
      )}
    </div>
  );
}
